import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { calculateLoanAhk } from "@/utils/ahk";
import { useAuth } from "@/context/AuthContext";
import { fmtDateIS } from "@/utils/formatDate";

/** Normalize a date string to DD.MM.YYYY — handles M.D.YYYY, D.M.YYYY, DD.MM.YYYY */
const normalizeDate = (d) => {
  if (!d || !d.includes('.')) return d;
  const parts = d.split('.');
  if (parts.length !== 3) return d;
  let a = parseInt(parts[0]), b = parseInt(parts[1]), year = parts[2];
  let day, month;
  if (a > 12) { day = a; month = b; }
  else if (b > 12) { month = a; day = b; }
  else if (parts[0].length === 2 && parts[0].startsWith('0')) { day = a; month = b; }
  else if (parts[1].length === 2 && parts[1].startsWith('0')) { day = a; month = b; }
  else if (b <= 12 && b >= 1 && a >= 1 && a <= 12 && b < a) { month = a; day = b; }
  else { day = a; month = b; }
  return `${String(day).padStart(2,'0')}.${String(month).padStart(2,'0')}.${year}`;
};

// Payment Schedule Component
export const PaymentSchedule = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [settings, setSettings] = useState({ reibor: 6.16, alag: 5.04 });
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [recentActions, setRecentActions] = useState([]);
  const [openMenuMonth, setOpenMenuMonth] = useState(null); // Track which overdue menu is open
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailType, setEmailType] = useState('notification');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailNewRate, setEmailNewRate] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Send email function
  const handleSendEmail = async () => {
    const targetEmail = emailTo || loan?.borrower_info?.email;
    if (!targetEmail || !targetEmail.includes('@')) {
      toast.error("Vinsamlegast sláðu inn gilt netfang");
      return;
    }

    if (!emailSubject || !emailMessage) {
      toast.error("Vinsamlegast fylltu út efni og skilaboð");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email/send-notification`, {
        to: targetEmail,
        customer_name: loan?.borrower_info?.full_name || '',
        subject_text: emailSubject,
        message: emailMessage
      });
      
      if (response.data?.success) {
        toast.success(`Tölvupóstur sendur til ${targetEmail}`, { duration: 5000 });
      } else {
        toast.success(`Sendur til ${targetEmail}`, { duration: 5000 });
      }
      setShowEmailDialog(false);
      setEmailSubject('');
      setEmailMessage('');
      setEmailTo('');
    } catch (error) {
      console.error("Error sending email:", error);
      const detail = error.response?.data?.detail || error.message || "Villa við að senda tölvupóst";
      toast.error(detail, { duration: 8000 });
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch loan
        const loansResponse = await axios.get(`${API}/loans`);
        const foundLoan = loansResponse.data.find(l => l.id === loanId);
        setLoan(foundLoan);
        
        // Fetch settings for REIBOR and Álag
        const settingsResponse = await axios.get(`${API}/settings`);
        if (settingsResponse.data) {
          setSettings(settingsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Villa við að sækja gögn");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [loanId]);

  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Vinsamlegast sláðu inn gilda upphæð");
      return;
    }

    try {
      const amount = parseFloat(paymentAmount);
      const currentBalance = loan.remaining_balance || loan.loan_amount;
      
      // Check if payment amount equals remaining balance
      const isFullPayment = Math.abs(amount - currentBalance) < 1; // Allow for small rounding differences
      
      await axios.post(`${API}/loans/${loanId}/payment`, {
        amount: amount,
        payment_date: paymentDate
      });
      
      // If full payment, update loan status to closed
      if (isFullPayment) {
        await axios.patch(`${API}/loans/${loanId}`, {
          status: 'closed'
        });
        toast.success("Innborgun skráð. Lán að fullu greitt - staða breytt í Lokið! 🎉");
      } else {
        toast.success("Innborgun skráð");
      }
      
      setShowPaymentDialog(false);
      setPaymentAmount('');
      
      // Refresh loan data
      const loansResponse = await axios.get(`${API}/loans`);
      const foundLoan = loansResponse.data.find(l => l.id === loanId);
      setLoan(foundLoan);
    } catch (error) {
      console.error("Error adding payment:", error);
      toast.error("Ekki tókst að skrá innborgun");
    }
  };

  const handleSendInvoice = async (row) => {
    try {
      let paydayCustomerId = loan.payday_customer_id;

      // Auto-create customer in Payday if missing
      if (!paydayCustomerId) {
        const kt = loan.borrower_info?.ssn || loan.borrower_info?.kennitala || loan.kennitala;
        const name = loan.borrower_info?.full_name || loan.borrower_info?.name || loan.name || "";
        if (!kt) {
          toast.error("Kennitala vantar á lánið");
          return;
        }
        toast.info("Stofna viðskiptavin í Payday...");
        // Try to find existing customer
        const fetchRes = await axios.get(`${API}/payday/fetch-customer/${kt}`);
        if (fetchRes.data.success && fetchRes.data.customer) {
          paydayCustomerId = fetchRes.data.customer.id;
        } else {
          // Create new customer
          const createRes = await axios.post(`${API}/payday/create-customer`, {
            kennitala: kt,
            name: name,
            email: loan.borrower_info?.email || "",
            phone: loan.borrower_info?.phone || "",
          });
          if (createRes.data.success) {
            paydayCustomerId = createRes.data.customer_id;
          } else {
            toast.error("Ekki tókst að stofna viðskiptavin í Payday");
            return;
          }
        }
        // Save to loan
        await axios.put(`${API}/loans/${loan.id}`, { payday_customer_id: paydayCustomerId });
        loan.payday_customer_id = paydayCustomerId;
        toast.success("Viðskiptavinur stofnaður í Payday");
      }
      
      // SAFEGUARD: Check if confirmation is required (from settings)
      const requireConfirmation = settings.require_invoice_confirmation ?? true; // Default true
      
      if (requireConfirmation) {
        // Show confirmation dialog with details
        const confirmed = window.confirm(
          `Staðfesta sendingu kröfu:\n\n` +
          `Mánuður: ${row.month}\n` +
          `Gjalddagi: ${row.date}\n` +
          `Upphæð: ${Math.round(row.payment).toLocaleString('is-IS')} kr\n\n` +
          `Viltu halda áfram?`
        );
        
        if (!confirmed) {
          console.log('User cancelled invoice sending');
          return;
        }
      }
      
      // Parse the due date from the row
      // Handle both DD.MM.YYYY and M.D.YYYY formats
      let day, month, year;
      if (row.date && row.date.includes('.')) {
        const parts = row.date.split('.');
        const a = parseInt(parts[0]);
        const b = parseInt(parts[1]);
        year = parseInt(parts[2]);
        
        // Detect format: if second part is small (1-12) and first part is also small,
        // check if first part > 12 → must be day. If both ≤ 12, 
        // check if second part looks like day=1 (payment dates are always 1st)
        if (a > 12) {
          // First part > 12, must be day (DD.MM.YYYY)
          day = a; month = b;
        } else if (b > 12) {
          // Second part > 12, must be day (M.D.YYYY) — unusual but handle
          month = a; day = b;
        } else if (parts[0].length === 2 && parts[0].startsWith('0')) {
          // Zero-padded first part like "01" → DD.MM.YYYY
          day = a; month = b;
        } else if (parts[1].length === 2 && parts[1].startsWith('0')) {
          // Zero-padded second part like "04" → DD.MM.YYYY
          day = a; month = b;
        } else if (b === 1 && a >= 1 && a <= 12) {
          // Second part is 1 (day=1st), first part is month → M.D.YYYY (e.g. "4.1.2026" = April 1st)
          month = a; day = b;
        } else {
          // Default: assume DD.MM.YYYY (Icelandic standard)
          day = a; month = b;
        }
      } else if (row.date && row.date.includes('/')) {
        const parts = row.date.split('/');
        day = parseInt(parts[0]);
        month = parseInt(parts[1]);
        year = parseInt(parts[2]);
      } else {
        const today = new Date();
        year = today.getFullYear();
        month = today.getMonth() + 1;
        day = today.getDate();
      }
      
      // Validate that we have all parts before creating Date
      if (!day || !month || !year) {
        throw new Error(`Invalid date components: day=${day}, month=${month}, year=${year}`);
      }
      
      const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      console.log('Parsed dueDate:', dueDate, 'ISO:', dueDate.toISOString());
      
      // Calculate eindagi (due date + configurable days from settings)
      // Use ?? instead of || to handle 0 correctly (0 is valid, not falsy)
      const eindagiDays = settings.eindagi_days_after ?? 4;
      const eindagiDate = new Date(dueDate);
      eindagiDate.setDate(eindagiDate.getDate() + eindagiDays);
      
      // Format dates for API (YYYY-MM-DD)
      const formatDateISO = (date) => {
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      
      const today = new Date();
      const invoiceDate = formatDateISO(today); // Bókunardagur
      const dueDateISO = formatDateISO(dueDate); // Gjalddagi
      const finalDueDate = formatDateISO(eindagiDate); // Eindagi
      
      // Prepare invoice payload
      const invoicePayload = {
        customer_id: loan.payday_customer_id,
        description: `Afborgun af láni - Mánuður ${row.month}`,
        invoiceDate: invoiceDate,
        dueDate: dueDateISO,
        finalDueDate: finalDueDate,
        currencyCode: "ISK",
        createClaim: true, // krafa í netbanka
        sendEmail: false,
        lines: [
          {
            description: `Afborgun af láni - Mánuður ${row.month}`,
            quantity: 1,
            unitPriceExcludingVat: Math.round(row.payment), // The Greiðsla amount
            vatPercentage: 0, // No VAT as specified (-)
            discountPercentage: 0
          }
        ]
      };
      
      // Show loading toast
      toast.loading("Býr til kröfu í Payday.is...", { id: 'invoice-create' });
      
      // Call backend to create invoice in Payday.is
      const response = await axios.post(`${API}/payday/create-invoice`, invoicePayload);
      
      toast.dismiss('invoice-create');
      
      if (response.data.success) {
        // Mark payment as invoice_sent and store Payday invoice data
        await axios.patch(
          `${API}/loans/${loanId}/scheduled-payment/${row.month}?status=invoice_sent&payday_invoice_id=${response.data.invoice_id}&payday_invoice_number=${response.data.invoice_number}`
        );
        
        toast.success(
          `Krófa send! Reikningur #${response.data.invoice_number} búinn til í Payday.is`,
          { duration: 5000 }
        );
        
        // Log action for undo capability
        const action = {
          id: Date.now(),
          type: 'invoice_sent',
          timestamp: new Date().toISOString(),
          month: row.month,
          date: row.date,
          amount: row.payment,
          invoiceNumber: response.data.invoice_number,
          invoiceId: response.data.invoice_id
        };
        setRecentActions(prev => [action, ...prev].slice(0, 10)); // Keep last 10 actions
        
        // Refresh loan data
        const loansResponse = await axios.get(`${API}/loans`);
        const foundLoan = loansResponse.data.find(l => l.id === loanId);
        setLoan(foundLoan);
      } else {
        toast.error(`Villa: ${response.data.message}`, { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss('invoice-create');
      console.error("Error creating invoice:", error);
      toast.error(
        `Ekki tókst að búa til kröfu. ${error.response?.data?.message || error.message}`,
        { duration: 8000 }
      );
    }
  };

  const handleMarkAsPaid = async (row) => {
    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Merkja greiðslu sem greidda:\n\n` +
        `Mánuður: ${row.month}\n` +
        `Gjalddagi: ${row.date}\n` +
        `Upphæð: ${Math.round(row.payment).toLocaleString('is-IS')} kr\n\n` +
        `Viltu halda áfram?`
      );
      
      if (!confirmed) {
        return;
      }
      
      toast.loading("Merkir sem greitt...", { id: 'mark-paid' });
      
      // Update payment status to 'paid'
      const response = await axios.patch(
        `${API}/loans/${loanId}/scheduled-payment/${row.month}?status=paid`
      );
      
      toast.dismiss('mark-paid');
      toast.success(`✓ Greiðsla fyrir Mánuð ${row.month} merkt sem greitt`, { duration: 5000 });
      
      // Refresh loan data
      const loansResponse = await axios.get(`${API}/loans`);
      const foundLoan = loansResponse.data.find(l => l.id === loanId);
      setLoan(foundLoan);
      
    } catch (error) {
      toast.dismiss('mark-paid');
      console.error("Error marking as paid:", error);
      toast.error(
        `Ekki tókst að merkja sem greitt. ${error.response?.data?.detail || error.message}`,
        { duration: 5000 }
      );
    }
  };


  const handleUndoInvoice = async (action) => {
    try {
      if (!window.confirm(`Hætta við kröfu fyrir Mánuð ${action.month}?\n\nÞetta mun endurstilla stöðu í 'pending' og þú getur sent hana aftur.`)) {
        return;
      }
      
      toast.loading("Hætti við kröfu...", { id: 'undo-invoice' });
      
      // Reset status back to pending
      await axios.patch(
        `${API}/loans/${loanId}/scheduled-payment/${action.month}?status=pending`
      );
      
      toast.dismiss('undo-invoice');
      toast.success("Krófa afturkölluð. Staða endurstillt í 'pending'.");
      
      // Remove from recent actions
      setRecentActions(prev => prev.filter(a => a.id !== action.id));
      
      // Refresh loan data
      const loansResponse = await axios.get(`${API}/loans`);
      const foundLoan = loansResponse.data.find(l => l.id === loanId);
      setLoan(foundLoan);
    } catch (error) {
      toast.dismiss('undo-invoice');
      console.error("Error undoing invoice:", error);
      toast.error("Ekki tókst að hætta við kröfu");
    }
  };

  const handleRefreshInvoiceStatus = async (row) => {
    try {
      const paydayInvoiceId = loan.payday_invoice_ids?.[row.month];
      
      if (!paydayInvoiceId) {
        toast.error("Enginn Payday reikningur fannst fyrir þessa greiðslu");
        return;
      }
      
      toast.loading("Sæki stöðu frá Payday.is...", { id: 'status-refresh' });
      
      // Fetch invoice status from Payday
      const response = await axios.get(`${API}/payday/invoice-status/${paydayInvoiceId}`);
      
      toast.dismiss('status-refresh');
      
      if (response.data.success) {
        const paydayStatus = response.data.status;
        const isPaid = response.data.paid;
        
        // Update local status based on Payday status
        let newStatus = 'invoice_sent';
        if (isPaid) {
          newStatus = 'paid';
          const today = new Date();
          const paidDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
          await axios.patch(`${API}/loans/${loanId}/scheduled-payment/${row.month}?status=paid&paid_date=${paidDate}`);
        }
        
        toast.success(`Staða uppfærð: ${isPaid ? 'Greitt' : paydayStatus}`);
        
        // Refresh loan data
        const loansResponse = await axios.get(`${API}/loans`);
        const foundLoan = loansResponse.data.find(l => l.id === loanId);
        setLoan(foundLoan);
      } else {
        toast.error(`Ekki tókst að sækja stöðu: ${response.data.message}`);
      }
    } catch (error) {
      toast.dismiss('status-refresh');
      console.error("Error refreshing invoice status:", error);
      toast.error("Ekki tókst að sækja stöðu frá Payday.is");
    }
  };

  if (loading) {
    return <div className="p-8">Hleður...</div>;
  }

  if (!loan) {
    return <div className="p-8">Lán fannst ekki</div>;
  }

  if (!loan.borrower_info || !loan.borrower_info.full_name) {
    return <div className="p-8">Villa: Ógildar upplýsingar um lántaka</div>;
  }

  // For legacy loans, use stored payment schedule. Otherwise calculate it.
  let schedule = [];
  let monthlyPayment = 0;
  // Calculate lantokugjald - treat 0 same as missing (fallback to formula)
  const lantokugjald = (loan.lantokugjald > 0) ? loan.lantokugjald : Math.max(29900, Math.min(89900, Math.round(loan.loan_amount * 0.089)));
  const amount = loan.loan_amount + lantokugjald; // Include origination fee
  const months = loan.requested_term_months;
  
  // Interest rate logic:
  // - Always use current variable rate from Stillingar (Vextir)
  // - This ensures payment schedules always reflect current market rates
  const loanRate = settings.vextir;
  
  if (loan.use_custom_schedule && loan.custom_payment_schedule && loan.custom_payment_schedule.length > 0) {
    // Use custom payment schedule with specific payment amounts
    const greidslus = loan.greidslus || settings.greidslus || 250;
    schedule = loan.custom_payment_schedule.map(item => ({
      month: item.month || 0,
      date: normalizeDate(item.date || ""),
      payment: item.amount || 0,
      principal: 0,  // Will be calculated below
      greidslus: greidslus,
      interest: 0,  // Will be calculated below
      balance: 0  // Will be calculated below
    }));
    
    // Calculate principal, interest, and balance for each payment
    let balance = amount;
    const rate = loanRate / 100;
    const monthlyRate = rate / 12;
    
    schedule = schedule.map(item => {
      const interest = Math.round(balance * monthlyRate);
      const principal = item.payment - interest - greidslus;
      balance -= principal;
      
      return {
        ...item,
        principal: Math.round(principal),
        interest: interest,
        balance: Math.max(0, Math.round(balance))
      };
    });
    
    monthlyPayment = schedule[0]?.payment || 0;
  } else if (loan.payment_schedule && loan.payment_schedule.length > 0) {
    // Use stored payment schedule (ensures payment dates never change after loan is activated)
    schedule = loan.payment_schedule.map(item => ({
      month: item.month_number || item.month || 0,
      date: normalizeDate(item.payment_date || item.date || ""),
      payment: item.payment_amount || item.payment || 0,
      principal: item.principal || 0,
      greidslus: item.fee || item.greidslus || 0,
      interest: item.interest || 0,
      balance: item.remaining_balance || item.balance || 0
    }));
    monthlyPayment = schedule[0]?.payment || 0;
  } else {
    // Calculate payment schedule using current variable rate (REIBOR + álag)
    const rate = loanRate / 100;
    const monthlyRate = rate / 12;
    const greidslus = loan.greidslus || settings.greidslus || 250;

    // Calculate total payments made (innborganir)
    const totalPaid = loan.payments ? loan.payments.reduce((sum, p) => sum + p.amount, 0) : 0;
    
    // Remaining balance after extra payments (innborganir)
    const remainingBalance = amount - totalPaid;
    
    // Recalculate monthly payment based on remaining balance (Option B: Reduce payment, keep term)
    // If extra payments have been made, recalculate monthly payment for remaining balance over full term
    monthlyPayment = remainingBalance * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    
    let balance = remainingBalance;
    const today = new Date();
    
    // Set starting date to first of next month
    const startDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    
    // Calculate initial interest for odd days (from today to first payment date)
    const daysInYear = 365;
    const daysBetween = Math.floor((startDate - today) / (1000 * 60 * 60 * 24));
    const dailyRate = rate / daysInYear;
    const initialInterest = remainingBalance * dailyRate * daysBetween;

    for (let i = 1; i <= months; i++) {
      let interest = balance * monthlyRate;
      let totalPayment = monthlyPayment + greidslus; // Add fee to payment (matches PDF calculation)
      
      // Add initial interest to first payment
      if (i === 1) {
        interest += initialInterest;
        totalPayment += initialInterest; // Increase first payment amount
      }
      
      const principal = monthlyPayment - interest + (i === 1 ? -initialInterest : 0);
      balance -= principal;

      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + (i - 1));
      // Ensure it's the first of the month
      paymentDate.setDate(1);

      schedule.push({
        month: i,
        date: fmtDateIS(paymentDate),
        payment: Math.round(totalPayment),
        principal: Math.round(principal),
        greidslus: Math.round(greidslus),
        interest: Math.round(interest),
        balance: Math.max(0, Math.round(balance))
      });
    }
    
    // Payment schedule calculated dynamically (no stored schedule in DB)
    console.warn("⚠️ Payment schedule not found in database - calculating dynamically.");
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4">
        <div className="mb-3">
          <div className="flex items-start gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/applications')}
              size="sm" className="h-7 text-xs px-2"
            >
              ← Til baka
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Forsendur láns</h1>
          </div>
        </div>

        <Card className="mb-2">
          <CardContent className="p-3">
            {loan && (() => {
              const lant = (loan.lantokugjald > 0) ? loan.lantokugjald : Math.max(29900, Math.min(89900, Math.round(loan.loan_amount * 0.089)));
              const amount = loan.loan_amount + lant;
              const months = loan.requested_term_months;
              // Show current system rate (variable rate loans tied to REIBOR)
              const loanRate = settings.vextir;
              const greidslus = loan.greidslus || settings.greidslus || 250;
              const schedule = loan.payment_schedule || [];
              const totalPaid = schedule.reduce((sum, p) => {
                const status = loan.scheduled_payments_status?.[p.month] || 'pending';
                return status === 'paid' ? sum + (p.payment || 0) : sum;
              }, 0) + (loan.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
              const monthsPaid = Object.values(loan.scheduled_payments_status || {}).filter(s => s === 'paid').length;
              const monthsRemaining = Math.max(0, months - monthsPaid);
              const lastPaidMonth = monthsPaid > 0 ? monthsPaid : 0;
              const balanceWithAccruedInterest = lastPaidMonth < schedule.length ? (schedule[lastPaidMonth]?.balance ?? loan.remaining_balance ?? (loan.loan_amount + lant)) : 0;

              const maxAhk = 0.35 + (settings.styrivextir || 7.25) / 100;
              const { ahk } = calculateLoanAhk(loan.loan_amount, lant, months, loanRate, greidslus);
              const isOver = ahk > 0 && ahk > maxAhk;
              const isUnder = ahk > 0 && ahk <= maxAhk;
              const borderColor = isOver ? 'border-red-500' : isUnder ? 'border-green-500' : 'border-gray-300';
              const textColor = isOver ? 'text-red-600' : isUnder ? 'text-green-600' : 'text-gray-600';

              return (
                <div>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                  {/* Row 1 */}
                  <div>
                    <p className="text-[10px] text-gray-500">Lántaki</p>
                    <p className="font-semibold text-xs">{loan.borrower_info.full_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Kennitala</p>
                    <p className="font-semibold text-xs">{loan.borrower_info.ssn || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Lánsfjárhæð</p>
                    <p className="font-semibold text-xs">{formatIcelandicNumber(Math.round(loan.loan_amount))} kr.</p>
                  </div>

                  {/* Row 2 - Vehicle info */}
                  {loan.vehicle_type && (
                    <div>
                      <p className="text-[10px] text-gray-500">Vegna</p>
                      <p className="font-semibold text-xs">{loan.vehicle_type}</p>
                    </div>
                  )}
                  {loan.registration_number && (
                    <div>
                      <p className="text-[10px] text-gray-500">Fastnúmer</p>
                      <p className="font-semibold text-xs">{loan.registration_number}</p>
                    </div>
                  )}
                  {(loan.vehicle_type || loan.registration_number) && !loan.vehicle_type && <div />}
                  {(loan.vehicle_type || loan.registration_number) && !loan.registration_number && <div />}
                  {(loan.vehicle_type && loan.registration_number) && <div />}

                  {/* Row 3 */}
                  <div>
                    <p className="text-[10px] text-gray-500">Lán nr.</p>
                    <p className="font-semibold text-xs">{loan.loan_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Lántökugjald</p>
                    <p className="font-semibold text-xs" data-testid="lantokugjald-value">{formatIcelandicNumber(Math.round(lant))} kr.</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Mánuðir eftir</p>
                    <p className="font-semibold text-xs">{monthsRemaining} mánuðir</p>
                  </div>

                  {/* Row 3 */}
                  <div>
                    <p className="text-[10px] text-gray-500">Lán stofnað</p>
                    <p className="font-semibold text-xs">{loan.created_at ? fmtDateIS(loan.created_at) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Eftirstöðvar</p>
                    <p className="font-semibold text-xs">{formatIcelandicNumber(Math.round(balanceWithAccruedInterest))} kr.</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Samtals greitt</p>
                    <p className="font-semibold text-xs">{formatIcelandicNumber(Math.round(totalPaid))} kr.</p>
                  </div>

                  {/* Row 4 */}
                  <div>
                    <p className="text-[10px] text-gray-500">Fyrsti gjalddagi</p>
                    <p className="font-semibold text-xs">{normalizeDate(loan.payment_schedule?.[0]?.date || loan.first_payment_date) || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Vextir</p>
                    <p className="font-semibold text-xs">{loanRate.toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Greiðsluseðill</p>
                    <p className="font-semibold text-xs">{formatIcelandicNumber(greidslus)} kr.</p>
                  </div>
                </div>

                {/* Contact info editing */}
                <div className="flex items-center gap-3 mt-2 pt-2 border-t">
                  <div className="flex items-center gap-1 flex-1">
                    <span className="text-[10px] text-gray-500 shrink-0">Netfang:</span>
                    <Input type="email" id="ps-email-input" defaultValue={loan.borrower_info.email || ''} className="h-6 text-xs" />
                  </div>
                  <div className="flex items-center gap-1 flex-1">
                    <span className="text-[10px] text-gray-500 shrink-0">Sími:</span>
                    <Input type="tel" id="ps-phone-input" defaultValue={loan.borrower_info.phone || ''} className="h-6 text-xs" />
                  </div>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-6 text-xs px-3" data-testid="ps-save-contact"
                    onClick={async () => {
                      const newEmail = document.getElementById('ps-email-input')?.value || '';
                      const newPhone = document.getElementById('ps-phone-input')?.value || '';
                      const updates = {};
                      if (newEmail !== (loan.borrower_info.email || '')) updates.email = newEmail;
                      if (newPhone !== (loan.borrower_info.phone || '')) updates.phone = newPhone;
                      if (Object.keys(updates).length === 0) { toast.info("Engar breytingar"); return; }
                      try {
                        await axios.patch(`${API}/loans/${loanId}`, { borrower_info: updates });
                        toast.success("Upplýsingar vistaðar");
                        const loansResponse = await axios.get(`${API}/loans`);
                        setLoan(loansResponse.data.find(l => l.id === loanId));
                      } catch (err) { toast.error("Villa við að vista"); }
                    }}
                  ><Check className="h-3 w-3 mr-1" />Vista</Button>
                  <span className={`w-10 h-10 rounded-full border-2 ${borderColor} flex items-center justify-center text-[9px] font-bold ${textColor} shrink-0`} title={`ÁHK: ${(ahk*100).toFixed(2)}% | Hámark: ${(maxAhk*100).toFixed(2)}%`}>
                    {(ahk * 100).toFixed(1)}%
                  </span>
                </div>

                {/* Row 4 - Buttons aligned right */}
                <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                  {loan.status === 'pending' && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-6 text-xs px-3 w-24 flex items-center justify-center gap-1"
                      data-testid="virkja-loan-btn"
                      onClick={async () => {
                        if (!window.confirm(`Virkja lán fyrir ${loan.borrower_info?.full_name}?\n\nLánið verður merkt sem Virk.\n\nViltu halda áfram?`)) return;
                        try {
                          await axios.patch(`${API}/loans/${loanId}`, { status: 'approved' });
                          toast.success("Lán virkjað!");
                          const loansResponse = await axios.get(`${API}/loans`);
                          setLoan(loansResponse.data.find(l => l.id === loanId));
                        } catch (error) { toast.error("Villa við virkjun"); }
                      }}
                    ><Check className="h-3 w-3" />Virkja</Button>
                  )}
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-6 text-xs px-3 w-24" onClick={() => setShowPaymentDialog(true)}>Innborgun</Button>
                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600 h-6 text-xs px-3 w-24 flex items-center justify-center gap-1"
                    onClick={async () => {
                      toast.loading("Uppfæri stöður...", { id: 'sync-loan' });
                      try {
                        const response = await axios.post(`${API}/loans/${loanId}/sync-payment-statuses`);
                        if (response.data.success) {
                          const updated = response.data.payments_updated;
                          if (updated > 0) { toast.success(`${updated} greiðslur uppfærðar`, { id: 'sync-loan', duration: 4000 }); }
                          else { toast.info("Engar breytingar", { id: 'sync-loan', duration: 3000 }); }
                          const loansResponse = await axios.get(`${API}/loans`);
                          setLoan(loansResponse.data.find(l => l.id === loanId));
                        }
                      } catch (error) { toast.error("Villa við uppfærslu", { id: 'sync-loan' }); }
                    }}
                  ><RefreshCw className="h-3 w-3" />Uppfæra</Button>
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 h-6 text-xs px-3 w-24 flex items-center justify-center gap-1"
                    onClick={() => { setEmailTo(loan?.borrower_info?.email || ''); setShowEmailDialog(true); }} data-testid="send-email-btn"
                  ><Mail className="h-3 w-3" />Senda</Button>
                  {loan.status !== 'closed' && (
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 h-6 text-xs px-2 flex items-center justify-center gap-1"
                      data-testid="close-loan-btn"
                      onClick={async () => {
                        if (!window.confirm(`Loka láni fyrir ${loan.borrower_info?.full_name}?\n\nLánið verður merkt sem Lokið. Þetta má afturkalla með því að breyta stöðu handvirkt.\n\nViltu halda áfram?`)) return;
                        try {
                          await axios.patch(`${API}/loans/${loanId}`, { status: 'closed' });
                          toast.success("Lán lokað — staða breytt í Lokið");
                          const loansResponse = await axios.get(`${API}/loans`);
                          setLoan(loansResponse.data.find(l => l.id === loanId));
                        } catch (error) { toast.error("Villa við lokun láns"); }
                      }}
                    ><span className="text-xs">Loka láni</span></Button>
                  )}
                  {loan.status === 'closed' && (
                    <Button size="sm" variant="outline" className="h-6 text-xs px-2 border-gray-400 text-gray-600 flex items-center gap-1"
                      data-testid="reopen-loan-btn"
                      onClick={async () => {
                        if (!window.confirm('Enduropna lán og breyta stöðu í Virkt?')) return;
                        try {
                          await axios.patch(`${API}/loans/${loanId}`, { status: 'disbursed' });
                          toast.success("Lán enduropnað — staða breytt í Virkt");
                          const loansResponse = await axios.get(`${API}/loans`);
                          setLoan(loansResponse.data.find(l => l.id === loanId));
                        } catch (error) { toast.error("Villa við enduropnun láns"); }
                      }}
                    ><span className="text-xs">Enduropna</span></Button>
                  )}
                  <Button size="sm" variant="outline" className="h-6 text-xs px-2 border-red-400 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
                    data-testid="delete-loan-btn"
                    onClick={async () => {
                      if (!window.confirm(`Eyða láni fyrir ${loan.borrower_info?.full_name}?\n\nÞessi aðgerð er óafturkræf.\n\nViltu halda áfram?`)) return;
                      try {
                        await axios.delete(`${API}/loans/${loanId}`);
                        toast.success("Láni eytt");
                        navigate('/applications');
                      } catch (error) { toast.error("Villa við eyðingu láns"); }
                    }}
                  ><Trash2 className="h-3 w-3" /><span className="text-xs">Eyða</span></Button>
                </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

            {/* Recent Actions Log with Undo */}
            {recentActions.length > 0 && (
              <div className="mb-4 border border-blue-200 rounded-lg p-3 bg-blue-50">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Nýlegar aðgerðir (Recent Actions)</h3>
                <div className="space-y-2">
                  {recentActions.map(action => (
                    <div key={action.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">
                            Mánuður {action.month} - {action.date}
                          </span>
                          <span className="text-xs text-gray-500">
                            {Math.round(action.amount).toLocaleString('is-IS')} kr
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          Reikningur #{action.invoiceNumber} • {new Date(action.timestamp).toLocaleTimeString('is-IS')}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] px-2 border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleUndoInvoice(action)}
                        title="Hætta við kröfu og endurstilla í pending"
                      >
                        Afturkalla
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}


        <Card>
          <CardContent className="p-3">
            <h2 className="text-lg font-bold mb-2">GREIÐSLUÁÆTLUN</h2>
            
            {/* Payment History */}
            {loan.payments && loan.payments.length > 0 && (
              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded">
                <h3 className="text-sm font-semibold text-green-800 mb-2">Innborganir</h3>
                <div className="space-y-1">
                  {loan.payments.map((payment, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">
                        {fmtDateIS(payment.payment_date)}
                      </span>
                      <span className="font-semibold text-green-700">
                        {formatIcelandicNumber(Math.round(payment.amount))} kr.
                      </span>
                    </div>
                  ))}
                  <div className="pt-0.5 mt-0.5 border-t border-green-300 flex justify-between font-semibold text-sm">
                    <span className="text-gray-700">Samtals innborgað:</span>
                    <span className="text-green-700">
                      {formatIcelandicNumber(Math.round(loan.payments.reduce((sum, p) => sum + p.amount, 0)))} kr.
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-1 text-left text-xs">Mán.</th>
                    <th className="border border-gray-300 px-2 py-1 text-left text-xs">Gjalddagi</th>
                    <th className="border border-gray-300 px-2 py-1 text-right text-xs">Greiðsla</th>
                    <th className="border border-gray-300 px-2 py-1 text-right text-xs">Höfuðstóll</th>
                    <th className="border border-gray-300 px-2 py-1 text-right text-xs">Greiðsluseðill</th>
                    <th className="border border-gray-300 px-2 py-1 text-right text-xs">Vextir</th>
                    <th className="border border-gray-300 px-2 py-1 text-right text-xs">Eftirstöðvar</th>
                    <th className="border border-gray-300 px-2 py-1 text-center text-xs">Staða/Aðgerð</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule
                    // Filter: For closed loans, only show paid months
                    .filter((row) => {
                      if (loan.status === 'closed') {
                        const paymentStatus = loan.scheduled_payments_status?.[row.month] || 'pending';
                        return paymentStatus === 'paid';
                      }
                      return true; // Show all months for non-closed loans
                    })
                    .map((row) => {
                    const paymentStatus = loan.scheduled_payments_status?.[row.month] || 'pending';
                    const isOverdue = paymentStatus === 'overdue';
                    
                    // Show "Senda kröfu" button only for loans with status "disbursed" (Virk) or "approved"
                    const showSendInvoiceButton = loan.status === 'disbursed' || loan.status === 'approved';
                    
                    // Compute if this is the next payment to be sent
                    let nextUnpaidMonthForRow = null;
                    for (const p of schedule) {
                      const pSt = loan.scheduled_payments_status?.[p.month] || 'pending';
                      if (pSt === 'pending') { nextUnpaidMonthForRow = p.month; break; }
                    }
                    if (!nextUnpaidMonthForRow) {
                      for (const p of schedule) {
                        const pSt = loan.scheduled_payments_status?.[p.month] || 'pending';
                        if (pSt === 'overdue') { nextUnpaidMonthForRow = p.month; break; }
                      }
                    }
                    const isNextToBeSent = row.month === nextUnpaidMonthForRow;
                    
                    return (
                    <tr key={row.month} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-2 py-0.5">{row.month}</td>
                      <td className={`border border-gray-300 px-2 ${loan.scheduled_payments_paid_dates?.[row.month] ? 'py-0.5' : 'py-1'} text-xs ${isOverdue ? 'text-red-600' : ''}`}>
                        <div className="leading-[1.1]">
                          <div className="text-[10px]">{row.date}</div>
                          {loan.scheduled_payments_paid_dates?.[row.month] && (
                            <div className="text-[8px] text-green-600 font-semibold">
                              Greitt: {loan.scheduled_payments_paid_dates[row.month]}
                            </div>
                          )}
                          {paymentStatus === 'invoice_sent' && (
                            <div className="text-[8px] text-yellow-600 font-semibold">Ógreitt</div>
                          )}
                          {isNextToBeSent && paymentStatus === 'pending' && showSendInvoiceButton && (
                            <div className="text-[8px] text-blue-600 font-semibold">→ Næsti</div>
                          )}
                        </div>
                      </td>
                      <td className={`border border-gray-300 px-2 py-1 text-right ${isOverdue ? 'text-red-600 font-bold' : ''}`}>
                        {(() => {
                          let displayAmount = row.payment;
                          
                          // Add penalty interest for overdue payments
                          if (isOverdue) {
                            // Parse date from "DD.MM.YYYY" format
                            const dateParts = row.date.split('.');
                            const dueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                            const penaltyRate = settings?.drattarvextir || 15.25;
                            const penalty = calculatePenaltyInterest(row.payment, dueDate, penaltyRate);
                            displayAmount = row.payment + penalty;
                          }
                          
                          return `${formatIcelandicNumber(Math.round(displayAmount))} kr.`;
                        })()}
                      </td>
                      <td className={`border border-gray-300 px-2 py-1 text-right ${isOverdue ? 'text-red-600' : ''}`}>{formatIcelandicNumber(row.principal)} kr.</td>
                      <td className={`border border-gray-300 px-2 py-1 text-right ${isOverdue ? 'text-red-600' : ''}`}>{formatIcelandicNumber(row.greidslus)} kr.</td>
                      <td className={`border border-gray-300 px-2 py-1 text-right ${isOverdue ? 'text-red-600' : ''}`}>{formatIcelandicNumber(row.interest)} kr.</td>
                      <td className={`border border-gray-300 px-2 py-1 text-right ${isOverdue ? 'text-red-600' : ''}`}>{formatIcelandicNumber(row.balance)} kr.</td>
                      <td className="border border-gray-300 px-2 py-1 text-center">
                        {(() => {
                          
                          if (paymentStatus === 'paid') {
                            // State 3: Paid - show status with paid date
                            const paidDate = loan.scheduled_payments_paid_dates?.[row.month];
                            return (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[10px] font-bold text-green-700">
                                  ✓ Greitt
                                </span>
                                {paidDate && (
                                  <span className="text-[8px] text-gray-500">
                                    {paidDate}
                                  </span>
                                )}
                              </div>
                            );
                          } else if (paymentStatus === 'overdue') {
                            // State 4: Overdue - show overdue status with action menu
                            const isMenuOpen = openMenuMonth === row.month;
                            
                            return (
                              <div className="flex items-center gap-1 relative">
                                <span className="text-[11px] text-red-600 font-extrabold">
                                  Í VANSKIL
                                </span>
                                <button
                                  className="text-red-500 hover:text-red-700"
                                  title="Senda viðvörun"
                                  onClick={() => {
                                    const dateParts = row.date.split('.');
                                    const dueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                                    const penaltyRate = settings?.drattarvextir || 15.25;
                                    const penalty = calculatePenaltyInterest(row.payment, dueDate, penaltyRate);
                                    const totalWithPenalty = Math.round(row.payment + penalty);
                                    setEmailSubject(`Viðvörun – Ógreidd krafa`);
                                    setEmailMessage(`Kæri ${loan.borrower_info?.full_name},\n\nVið viljum vekja athygli á að krafa með gjalddaga ${row.date} er ógreidd samkvæmt okkar bókhaldi og hefur nú fallið í vanskil.\n\nVið biðjum þig vinsamlegast um að ganga frá greiðslu sem allra fyrst. Ef greiðsla hefur þegar verið innt af hendi, biðjumst við velvirðingar og vinsamlegast líttu á þessa tilkynningu sem ógilda.\n\nMeð kveðju,\nFONDO ehf.`);
                                    setEmailTo(loan?.borrower_info?.email || '');
                                    setShowEmailDialog(true);
                                  }}
                                >
                                  <Mail className="h-3 w-3" />
                                </button>
                                {/* Red sphere button */}
                                <button
                                  onClick={() => setOpenMenuMonth(isMenuOpen ? null : row.month)}
                                  className="w-2.5 h-2.5 rounded-full bg-red-600 hover:bg-red-700 cursor-pointer border-0 p-0"
                                  title="Aðgerðir"
                                />
                                
                                {/* Dropdown menu */}
                                {isMenuOpen && (
                                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[120px]">
                                    <button
                                      className="block w-full text-left px-3 py-1.5 text-[10px] hover:bg-gray-100"
                                      onClick={async () => {
                                        setOpenMenuMonth(null);
                                        if (window.confirm(`Merkja greiðslu fyrir mánuð ${row.month} sem greidda?`)) {
                                          try {
                                            toast.loading("Uppfæri stöðu...", { id: 'mark-paid' });
                                            
                                            const updatedStatus = { ...loan.scheduled_payments_status, [row.month]: 'paid' };
                                            
                                            await axios.patch(`${API}/loans/${loan.id}`, {
                                              scheduled_payments_status: updatedStatus
                                            });
                                            
                                            toast.success("Greiðsla merkt sem greidd!", { id: 'mark-paid' });
                                            window.location.reload();
                                          } catch (error) {
                                            console.error("Error marking as paid:", error);
                                            toast.error("Villa kom upp", { id: 'mark-paid' });
                                          }
                                        }
                                      }}
                                    >
                                      ✓ Greitt
                                    </button>
                                    <button
                                      className="block w-full text-left px-3 py-1.5 text-[10px] hover:bg-gray-100 border-t border-gray-200"
                                      onClick={() => {
                                        setOpenMenuMonth(null);
                                        const dateParts = row.date.split('.');
                                        const dueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                                        const penaltyRate = settings?.drattarvextir || 15.25;
                                        const penalty = calculatePenaltyInterest(row.payment, dueDate, penaltyRate);
                                        const totalWithPenalty = Math.round(row.payment + penalty);
                                        setEmailSubject(`Viðvörun – Vanskil mánuður ${row.month}`);
                                        setEmailMessage(`Kæri/kæra ${loan.borrower_info?.full_name},\n\nGreiðsla fyrir mánuð ${row.month} að upphæð ${formatIcelandicNumber(Math.round(row.payment))} kr. með gjalddaga ${row.date} er í vanskilum.\n\nDráttarvextir: ${formatIcelandicNumber(Math.round(penalty))} kr.\nHeild með dráttarvöxtum: ${formatIcelandicNumber(totalWithPenalty)} kr.\n\nVinsamlegast greiðið kröfuna sem allra fyrst til að forðast frekari dráttarvexti.\n\nMeð kveðju,\nFONDO ehf.`);
                                        setEmailTo(loan?.borrower_info?.email || '');
                                        setShowEmailDialog(true);
                                      }}
                                    >
                                      <Mail className="h-3 w-3 inline mr-1" />Senda viðvörun
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          } else if (paymentStatus === 'invoice_sent') {
                            // State 2: Invoice sent - Ógreitt shown under date, action buttons here
                            const invoiceNumber = loan.payday_invoice_numbers?.[row.month];
                            return (
                              <div className="flex flex-col gap-0.5 items-center">
                                {invoiceNumber && (
                                  <span className="text-[8px] text-gray-500">
                                    #{invoiceNumber}
                                  </span>
                                )}
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    className="h-5 text-[8px] px-2 py-0 border border-red-300 text-red-600 hover:bg-red-50 bg-transparent w-[70px]"
                                    onClick={() => {
                                      setEmailSubject(`Viðvörun – Ógreidd krafa`);
                                      setEmailMessage(`Kæri ${loan.borrower_info?.full_name},\n\nVið viljum vekja athygli á að krafa með gjalddaga ${row.date} er ógreidd samkvæmt okkar bókhaldi og hefur nú fallið í vanskil.\n\nVið biðjum þig vinsamlegast um að ganga frá greiðslu sem allra fyrst. Ef greiðsla hefur þegar verið innt af hendi, biðjumst við velvirðingar og vinsamlegast líttu á þessa tilkynningu sem ógilda.\n\nMeð kveðju,\nFONDO ehf.`);
                                      setEmailTo(loan?.borrower_info?.email || '');
                                      setShowEmailDialog(true);
                                    }}
                                  >
                                    <Mail className="h-2.5 w-2.5 mr-0.5" />Viðvörun
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-5 text-[8px] px-2 py-0 border border-yellow-400 text-yellow-700 hover:bg-yellow-50 bg-transparent w-[70px]"
                                    onClick={async () => {
                                    if (window.confirm(`Viltu hætta við kröfu fyrir mánuð ${row.month}? Þú getur sent hana aftur.`)) {
                                      try {
                                        toast.loading("Hætti við kröfu...", { id: 'cancel-invoice' });
                                        
                                        // Reset status back to pending and clear invoice IDs
                                        await axios.patch(
                                          `${API}/loans/${loanId}/scheduled-payment/${row.month}?status=pending`
                                        );
                                        
                                        toast.dismiss('cancel-invoice');
                                        toast.success("Krófa hætt við. Þú getur nú sent hana aftur.");
                                        
                                        // Refresh loan data
                                        const loansResponse = await axios.get(`${API}/loans`);
                                        const foundLoan = loansResponse.data.find(l => l.id === loanId);
                                        setLoan(foundLoan);
                                      } catch (error) {
                                        toast.dismiss('cancel-invoice');
                                        console.error("Error canceling invoice:", error);
                                        toast.error("Ekki tókst að hætta við kröfu");
                                      }
                                    }
                                  }}
                                >
                                  Hætta við
                                </Button>
                                </div>
                              </div>
                            );
                          } else {
                            // State 1: Pending - green button "senda kröfu" (only visible for active loans)
                            if (!showSendInvoiceButton) {
                              return (
                                <span className="text-[9px] text-gray-400">
                                  (Aðeins virk lán)
                                </span>
                              );
                            }
                            
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    className={`${
                                      isNextToBeSent 
                                        ? 'border border-green-400 text-green-700 hover:bg-green-50 bg-transparent' 
                                        : 'border border-gray-300 text-gray-400 cursor-not-allowed bg-transparent'
                                    } h-5 text-[8px] px-2 py-0 w-[70px]`}
                                    onClick={() => isNextToBeSent && handleSendInvoice(row)}
                                    disabled={!isNextToBeSent}
                                    title={isNextToBeSent ? 'Senda kröfu' : 'Vinsamlegast sendu fyrri greiðslur fyrst'}
                                  >
                                    {isNextToBeSent ? 'Senda kröfu' : `Mánuður ${row.month}`}
                                  </Button>
                                  
                                  {isNextToBeSent && (
                                    <Button
                                      size="sm"
                                      className="border border-yellow-400 text-yellow-700 hover:bg-yellow-50 bg-transparent h-5 text-[8px] px-2 py-0 w-[70px]"
                                      onClick={() => handleMarkAsPaid(row)}
                                      title="Merkja sem greitt"
                                    >
                                      Greitt
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Message for closed loans */}
            {loan.status === 'closed' && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800 text-center">
                  ✓ Lán lokað - Greitt með Nýju láni (Skuldbreyting)
                </p>
              </div>
            )}

            {/* Hide summary for legacy loans - they have pre-defined payment schedules */}
            {!loan.is_legacy && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Mánaðarleg greiðsla</p>
                    <p className="text-lg font-bold text-blue-600">{formatIcelandicNumber(Math.round(monthlyPayment))} kr.</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Heildargreiðsla</p>
                    <p className="text-lg font-bold text-gray-900">{formatIcelandicNumber(Math.round(monthlyPayment * months))} kr.</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Skrá innborgun</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {loan && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <p className="text-[10px] text-gray-600">Eftirstöðvar (Remaining balance)</p>
                <p className="text-xs font-semibold text-blue-700">
                  {(() => {
                    // Calculate remaining balance including penalty interest
                    let balance = loan.remaining_balance || loan.loan_amount;
                    
                    // Add accumulated penalty interest from all overdue payments
                    if (loan.scheduled_payments_status && schedule) {
                      Object.entries(loan.scheduled_payments_status).forEach(([monthNum, status]) => {
                        if (status === 'overdue') {
                          const scheduleEntry = schedule.find(s => s.month === parseInt(monthNum));
                          if (scheduleEntry && scheduleEntry.date) {
                            // Parse date from "DD.MM.YYYY" format
                            const dateParts = scheduleEntry.date.split('.');
                            const dueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                            const penaltyRate = settings?.drattarvextir || 15.25;
                            const penalty = calculatePenaltyInterest(scheduleEntry.payment, dueDate, penaltyRate);
                            balance += penalty;
                          }
                        }
                      });
                    }
                    
                    return formatIcelandicNumber(Math.round(balance));
                  })()} kr.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="payment-amount" className="text-[10px]">Upphæð</Label>
              <Input
                id="payment-amount"
                type="number"
                placeholder="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="payment-date" className="text-[10px]">Dagsetning</Label>
              <Input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2"
                onClick={() => {
                  setShowPaymentDialog(false);
                  setPaymentAmount('');
                }}
              >
                Hætta við
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 h-6 text-[10px] px-2"
                onClick={handleAddPayment}
              >
                Skrá innborgun
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Senda til {loan?.borrower_info?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Customer email - always editable */}
            <div>
              <Label className="text-[10px]">Netfang viðskiptavinar</Label>
              <Input
                type="email"
                placeholder="netfang@example.is"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="h-7 text-xs"
              />
            </div>

            <div>
              <Label className="text-[10px]">Efni (Subject)</Label>
              <Input
                placeholder="Efni tölvupósts..."
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px]">Skilaboð</Label>
              <Textarea
                placeholder="Skrifaðu skilaboð hér..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="text-xs min-h-[100px]"
              />
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setShowEmailDialog(false);
                  setEmailSubject('');
                  setEmailMessage('');
                  setEmailTo('');
                }}
              >
                Hætta við
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 h-7 text-xs flex items-center gap-1"
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailTo || !emailTo.includes('@')}
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Sendi...
                  </>
                ) : (
                  <>
                    <Send className="h-3 w-3" />
                    Senda
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

